import {type SyntheticEvent, useMemo, useState} from "react";

import {handleError} from "services/notification/handlers";

const objKeys = <T extends Record<string, unknown>>(obj: T) =>
  Object.keys(obj) as Array<keyof T>;

export type FieldValidationError = string | undefined;

// consider "string (non-empty)" as error; undefined or empty string means no error
const isError = (error: FieldValidationError) =>
  typeof error === "string" && error.length > 0;

export type FieldValidator<T> = (value: T) => FieldValidationError;

type OnChangeHandler<V> = (value: V) => void;
type OnBlurHandler = () => void;

type ValueState<T> = {
  value: T;
  error?: FieldValidationError;
  touched: boolean;
};

// [DefaultValue, Validator]
export type FieldConfig<T> = [T, FieldValidator<T>];

type Value<T> = T extends FieldConfig<infer U> ? U : never;

type ActionMap<T extends Record<string, FieldConfig<unknown>>> = {
  [K in keyof T]: OnChangeHandler<Value<T[K]>>;
};

type BlurMap<T extends Record<string, FieldConfig<unknown>>> = {
  [K in keyof T]: OnBlurHandler;
};

type ValueStateMap<T extends Record<string, FieldConfig<unknown>>> = {
  [K in keyof T]: ValueState<Value<T[K]>>;
};

type ValueMap<T extends Record<string, FieldConfig<unknown>>> = {
  [K in keyof T]: Value<T[K]>;
};

export type FormField<T> = {
  value: T;
  error?: FieldValidationError;
  onChange: OnChangeHandler<T>;
  onBlur: OnBlurHandler;
  label?: string;
};

export type FieldMap<T extends Record<string, FieldConfig<unknown>>> = {
  [K in keyof T]: FormField<Value<T[K]>>;
};

type UseFormOptions<T extends Record<string, FieldConfig<unknown>>> = {
  fields: T;
  onSubmit: (data: ValueMap<T>) => Promise<void>;
};

type UseFormResult<T extends Record<string, FieldConfig<unknown>>> = {
  handleSubmit: (event?: SyntheticEvent<unknown>) => void;
  fields: FieldMap<T>;
  submitting: boolean;
  resetFields: (newFields?: {[K in keyof T]: Value<T[K]>}) => void;
  setFieldError: (key: keyof T, error: string) => void;
};

const useForm = <T extends Record<string, FieldConfig<any>>>(
  options: UseFormOptions<T>,
): UseFormResult<T> => {
  // Capture the initial config once and keep it stable.
  const initialConfig = useMemo(() => options.fields, []);

  // Initialize values without running validators (we validate on touch/submit)
  const [values, setValues] = useState(() => {
    const initialValues = {} as ValueStateMap<T>;
    for (const key of objKeys(initialConfig)) {
      const [value] = initialConfig[key];
      initialValues[key] = {
        value: value as Value<T[keyof T]>,
        error: undefined,
        touched: false,
      };
    }
    return initialValues;
  });

  // Actions to update value (validate only if touched)
  const updateActions = useMemo(() => {
    const actions = {} as ActionMap<T>;
    for (const key of objKeys(initialConfig)) {
      const validator = initialConfig[key][1];
      actions[key] = (value: Value<T[keyof T]>) => {
        setValues(prev => {
          const prevField = prev[key];
          const touched = prevField?.touched ?? false;

          const validationError = touched ? validator(value) : undefined;

          return {
            ...prev,
            [key]: {
              value,
              touched,
              error: validationError,
            },
          };
        });
      };
    }
    return actions;
  }, [initialConfig]);

  // Blur handlers to mark touched and validate that field
  const blurActions = useMemo(() => {
    const blurs = {} as BlurMap<T>;
    for (const key of objKeys(initialConfig)) {
      const validator = initialConfig[key][1];
      blurs[key] = () => {
        setValues(prev => {
          const field = prev[key];
          const error = validator(field.value);
          return {
            ...prev,
            [key]: {
              ...field,
              touched: true,
              error,
            },
          };
        });
      };
    }
    return blurs;
  }, [initialConfig]);

  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Run full validation over all fields and return whether valid
  const validateAll = (): {valid: boolean; updated: ValueStateMap<T>} => {
    const updated = {...values} as ValueStateMap<T>;
    let hasError = false;
    for (const key of objKeys(initialConfig)) {
      const validator = initialConfig[key][1];
      const value = values[key].value as Value<T[typeof key]>;
      const error = validator(value);
      updated[key] = {
        ...updated[key],
        error,
        touched: true,
      };
      if (isError(error)) hasError = true;
    }
    return {valid: !hasError, updated};
  };

  const handleSubmit = (event?: SyntheticEvent<unknown>) => {
    event?.preventDefault();
    event?.stopPropagation();

    // validate everything now
    const {valid, updated} = validateAll();

    // show errors and update state so fields render errors
    setValues(updated);
    setShowErrors(true);

    if (!valid) return;

    const data = {} as ValueMap<T>;
    for (const key of objKeys(updated)) data[key] = updated[key].value;

    setSubmitting(true);
    options
      .onSubmit(data)
      .catch(err => handleError(err))
      .finally(() => setSubmitting(false));
  };

  const resetFields = (newFields?: {[K in keyof T]: Value<T[K]>}) => {
    setValues(prev => {
      const updated = {...prev};

      for (const key of objKeys(prev)) {
        const defaultValue = initialConfig[key][0];

        const valueToSet = newFields?.[key] ?? defaultValue;

        updated[key] = {
          value: valueToSet as Value<T[typeof key]>,
          error: undefined,
          touched: false,
        };
      }

      return updated;
    });

    setShowErrors(false);
  };

  const setFieldError = (key: keyof T, error: string) => {
    setValues(prev => ({
      ...prev,
      [key]: {...prev[key], error},
    }));
  };

  const fields = {} as FieldMap<T>;
  for (const key of objKeys(values)) {
    fields[key] = {
      value: values[key].value,
      // show error only when field touched or form submit attempted
      error: showErrors || values[key].touched ? values[key].error : undefined,
      onChange: updateActions[key],
      onBlur: blurActions[key],
    };
  }

  return {
    handleSubmit,
    fields,
    submitting,
    resetFields,
    setFieldError,
  };
};

export default useForm;
